import {
  useV2GetProjectReviewByIdQuery,
  V2ProjectReviewResource,
} from "@generated";
import { CatalogBias, ProjectReview, ServiceDelivery } from "../types";

// Typed off the generated resource itself, not erased to Record<string,
// unknown>, and read by destructuring (not bracket-index access): this
// repo's tsconfig sets noImplicitAny:false, under which `obj["typo-key"]`
// silently resolves to `any` even when `obj`'s type has no index signature.
// Destructuring a nonexistent literal key is still flagged by tsc under that
// same setting, so it is the only one of the two that gives a misspelled
// dasherized key an actual compile error.
type ApiService = NonNullable<
  V2ProjectReviewResource["attributes"]["services"]
>[number];
type ApiBias = NonNullable<ApiService["catalog-bias"]>;

const toBias = (bias: ApiBias | undefined): CatalogBias | undefined => {
  if (!bias) return undefined;

  const {
    direction,
    "bias-percentage": biasPercentage,
    confidence,
    "observation-count": observationCount,
  } = bias;

  return {
    direction: direction ?? "none",
    biasPercentage,
    confidence,
    observationCount: observationCount ?? 0,
  };
};

const toService = (service: ApiService): ServiceDelivery => {
  const {
    name,
    status,
    settled,
    coverage,
    "estimated-hours": estimatedHours,
    "actual-hours": actualHours,
    "delta-hours": deltaHours,
    // No ?? fallback: null means the service carried no estimate, not 0%.
    "variance-percentage": variancePercentage,
    "closed-on": closedOn,
    "catalog-bias": catalogBias,
  } = service;

  return {
    name: name ?? "",
    status: status ?? "",
    settled: settled === true,
    coverage: coverage === "partial" ? "partial" : "full",
    estimatedHours: estimatedHours ?? 0,
    actualHours: actualHours ?? 0,
    deltaHours: deltaHours ?? 0,
    variancePercentage,
    closedOn,
    catalogBias: toBias(catalogBias),
  };
};

const useGetProjectReview = (id: string) => {
  const { data, error, isLoading, isFetching, refetch } =
    useV2GetProjectReviewByIdQuery({ id }, { skip: !id });

  let review: ProjectReview | null = null;

  if (data?.data) {
    const {
      "project-name": projectName,
      "client-name": clientName,
      "project-status": projectStatus,
      "last-completed-on": lastCompletedOn,
      "services-tracked": servicesTracked,
      "services-settled": servicesSettled,
      "unsettled-services": unsettledServices,
      "services-partial": servicesPartial,
      "estimated-hours": estimatedHours,
      "actual-hours": actualHours,
      "delta-hours": deltaHours,
      "variance-percentage": variancePercentage,
      services,
    } = data.data.attributes;

    review = {
      id: String(data.data.id ?? id),
      projectName: projectName ?? "",
      clientName: clientName ?? "",
      projectStatus: projectStatus ?? "",
      lastCompletedOn: lastCompletedOn ?? "",
      servicesTracked: servicesTracked ?? 0,
      servicesSettled: servicesSettled ?? 0,
      unsettledServices: unsettledServices ?? 0,
      servicesPartial: servicesPartial ?? 0,
      estimatedHours: estimatedHours ?? 0,
      actualHours: actualHours ?? 0,
      deltaHours: deltaHours ?? 0,
      variancePercentage,
      services: (services ?? []).map(toService),
    };
  }

  return { review, isLoading, isFetching, error, refetch };
};

export default useGetProjectReview;
